import { apiClient } from "@/lib/api";
import {
  ExchangeRateDto,
  ExchangeRateHistoryDto,
  UpdateExchangeRateRequest,
  ExchangeRateHistoryFilters,
} from "@/types/exchangeRate";
import { formatExchangeRate } from "@/lib/currencyUtils";
import { PaginationInfo } from "@/types/api-response";

/**
 * Backend DTO structure (what the API returns)
 */
interface BackendExchangeRateDto {
  id: string;
  targetCurrency: string;
  rate: number;
  effectiveFrom: string;
  expiresAt?: string | null;
  isActive: boolean;
  source: string;
  notes?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Backend History DTO structure
 */
interface BackendExchangeRateHistoryDto {
  id: string;
  targetCurrency: string;
  rate: number;
  effectiveFrom: string;
  expiresAt?: string | null;
  isActive: boolean;
  source: string;
  notes?: string | null;
  activeDurationHours?: number | null;
}

/**
 * Transform backend DTO to frontend DTO format
 */
function transformExchangeRateDto(
  backendDto: BackendExchangeRateDto,
): ExchangeRateDto {
  return {
    currency: backendDto.targetCurrency,
    rateToVND: backendDto.rate,
    displayRate: formatExchangeRate(backendDto.targetCurrency, backendDto.rate),
    version: backendDto.version,
    effectiveFrom: backendDto.effectiveFrom,
    updatedAt: backendDto.updatedAt,
    isCurrent: backendDto.isActive,
  };
}

/**
 * Transform backend history DTO to frontend history DTO format
 */
function transformExchangeRateHistoryDto(
  backendDto: BackendExchangeRateHistoryDto,
): ExchangeRateHistoryDto {
  return {
    currency: backendDto.targetCurrency,
    rateToVND: backendDto.rate,
    displayRate: formatExchangeRate(backendDto.targetCurrency, backendDto.rate),
    version: 0, // History DTO doesn't include version in backend
    effectiveFrom: backendDto.effectiveFrom,
    effectiveTo: backendDto.expiresAt || undefined,
    source: backendDto.source,
    notes: backendDto.notes || undefined,
    isCurrent: backendDto.isActive,
  };
}

/**
 * Admin exchange rate service for managing currency exchange rates.
 *
 * All endpoints return StandardResponse<T> or PaginatedStandardResponse<T> formats
 * with proper error handling and type safety.
 *
 * Base currency: VND (Vietnamese Dong)
 * All rates are expressed as: 1 [Currency] = X VND
 */
export const adminExchangeRateService = {
  /**
   * Gets all current exchange rates.
   * Returns the currently active exchange rate for all supported currencies.
   *
   * @returns List of current exchange rates
   * @throws Error if response is an error response
   */
  async getExchangeRates(): Promise<ExchangeRateDto[]> {
    interface ExchangeRatesResponse {
      success: boolean;
      data: BackendExchangeRateDto[];
      message?: string;
      error?: { message: string };
    }

    const response = await apiClient.get<ExchangeRatesResponse>(
      "/api/v1/admin/exchange-rates",
    );

    if (!response.data.success || response.data.error) {
      throw new Error(
        response.data.error?.message ||
          response.data.message ||
          "Failed to fetch exchange rates",
      );
    }

    return response.data.data.map(transformExchangeRateDto);
  },

  /**
   * Gets a specific exchange rate by currency code.
   *
   * @param currency - Currency code (e.g., "USD", "EUR")
   * @returns Current exchange rate for the currency
   * @throws Error if currency not found or response is an error response
   */
  async getExchangeRate(currency: string): Promise<ExchangeRateDto> {
    interface ExchangeRateResponse {
      success: boolean;
      data: BackendExchangeRateDto;
      message?: string;
      error?: { message: string };
    }

    const response = await apiClient.get<ExchangeRateResponse>(
      `/api/v1/admin/exchange-rates/${currency}`,
    );

    if (!response.data.success || response.data.error) {
      throw new Error(
        response.data.error?.message ||
          response.data.message ||
          "Failed to fetch exchange rate",
      );
    }

    return transformExchangeRateDto(response.data.data);
  },

  /**
   * Updates an exchange rate.
   * Creates a new rate version and marks the previous one as historical.
   *
   * @param currency - Currency code to update
   * @param request - Update request with new rate and optional notes
   * @returns Updated exchange rate with new version
   * @throws Error if currency not found, validation fails, or response is an error response
   */
  async updateExchangeRate(
    currency: string,
    request: UpdateExchangeRateRequest,
  ): Promise<ExchangeRateDto> {
    interface UpdateExchangeRateResponse {
      success: boolean;
      data: BackendExchangeRateDto;
      message?: string;
      error?: { message: string };
    }

    const response = await apiClient.put<UpdateExchangeRateResponse>(
      `/api/v1/admin/exchange-rates/${currency}`,
      request,
    );

    if (!response.data.success || response.data.error) {
      throw new Error(
        response.data.error?.message ||
          response.data.message ||
          "Failed to update exchange rate",
      );
    }

    return transformExchangeRateDto(response.data.data);
  },

  /**
   * Gets exchange rate history for a currency.
   * Returns all historical rates with optional date range filtering.
   *
   * @param filters - Filter parameters including currency, date range, and pagination
   * @returns Paginated list of historical exchange rates
   * @throws Error if currency not found or response is an error response
   */
  async getExchangeRateHistory(
    filters: ExchangeRateHistoryFilters,
  ): Promise<{ data: ExchangeRateHistoryDto[]; pagination: PaginationInfo }> {
    interface HistoryResponse {
      success: boolean;
      data: BackendExchangeRateHistoryDto[];
      pagination: {
        page: number;
        itemsPerPage: number;
        totalItems: number;
        totalPages: number;
        hasPrev: boolean;
        hasNext: boolean;
      };
      message?: string;
      error?: { message: string };
    }

    const response = await apiClient.get<HistoryResponse>(
      `/api/v1/admin/exchange-rates/${filters.currency}/history`,
      {
        params: {
          page: filters.page ?? 1,
          limit: filters.pageSize ?? 20,
          ...(filters.fromDate && { from: filters.fromDate }),
          ...(filters.toDate && { to: filters.toDate }),
        },
      },
    );

    if (!response.data.success || response.data.error) {
      throw new Error(
        response.data.error?.message ||
          response.data.message ||
          "Failed to fetch exchange rate history",
      );
    }

    // Transform backend pagination to standard PaginationInfo format
    const backendPagination = response.data.pagination;
    return {
      data: response.data.data.map(transformExchangeRateHistoryDto),
      pagination: {
        currentPage: backendPagination.page,
        itemsPerPage: backendPagination.itemsPerPage,
        totalItems: backendPagination.totalItems,
        totalPages: backendPagination.totalPages,
        hasNext: backendPagination.hasNext,
        hasPrev: backendPagination.hasPrev,
      },
    };
  },

  /**
   * Gets supported currencies list.
   * Returns all currencies that can have exchange rates configured.
   *
   * @returns Array of currency codes
   * @throws Error if response is an error response
   */
  async getSupportedCurrencies(): Promise<string[]> {
    interface CurrenciesResponse {
      success: boolean;
      data: string[];
      message?: string;
      error?: { message: string };
    }

    const response = await apiClient.get<CurrenciesResponse>(
      "/api/v1/admin/exchange-rates/currencies",
    );

    if (!response.data.success || response.data.error) {
      throw new Error(
        response.data.error?.message ||
          response.data.message ||
          "Failed to fetch currencies",
      );
    }

    return response.data.data;
  },
};
